import React from 'react';
import { observer } from 'mobx-react';
import { DataGrid, InputDropdown, SearchToolbar, View, ViewHeader } from '../../../../../core/components/Index';
import { useNavigate } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { SubmissionListModel } from './SubmissionListModel';
import { Column } from 'primereact/column';
import Helper, { formatDate } from '../../../../../core/services/Helper';
import SraModule from '../../SraModule';
import { Appl } from '../../../../../core/Appl';
import SubmissionListViewModel from './SubmissionListViewModel';
import { DataTableSelectEvent } from 'primereact/datatable';
import { Button as PrimeButton } from 'primereact/button';
import { FileInput } from 'lucide-react';
import { AppPermissions } from '../../../../../core/services/AppPermissions';

export const SubmissionListView: React.FC = observer(() => {
  const vm = Appl.Services.get<SubmissionListViewModel>('SubmissionListViewModel');
  vm.navigate = useNavigate();

  useAsyncEffect(async () => {
    await vm.initAsync();
    await vm.getPagedAsync();
  }, []);

  const retirementEffectiveDateTemplate = (row: SubmissionListModel) => {
    return formatDate(row.retirementEffDate);
  };
  const receivedDateTemplate = (row: SubmissionListModel) => {
    return formatDate(row.received);
  };

  const onRowSelect = (event: DataTableSelectEvent) => {
    let editLink = `/sra/retiree/addEdit/${event.data.submissionId}`;
    if (event.data.submissionStatus !== "Draft") {
      vm.navigate(editLink);
    }
  };

  return (
    <>
      <View id="RetireListView" pageTitle={vm.pageTitle}
        authKey={AppPermissions.ORAIntake_4_3_VIEW}
        showMenuBar={false}
        menuItems={SraModule.MenuItems()}
      >
        <ViewHeader title={vm.pageTitle} description={vm.pageDescription}>
          <InputDropdown
            id="lovSubmissionStatus"
            value={vm.lovSubmissionStatus}
            options={vm.submissionStatusList}
            optionLabel="value"
            optionValue="value"
            placeholder='Select'
            className="w-full me-2 mt-1"
            required={false}
            onChange={async (e) => await vm.onStatusChangeAsync(e)}
          />
          <SearchToolbar
            filterPlaceholder="ID / Name / Phone / Email / PIN"
            filter={vm.searchRequest?.filter}
            onFilterChange={(e) => vm.onInputChange(e, 'filter', vm.searchRequest)}
            onRefresh={async () => await vm.getPagedAsync()}
            addNewVisible={false}
            refreshVisible={false}
            showButton={false}
            // run search on input change
            searchOnInputChange={true}
          />
        </ViewHeader>
        <div className="flex space-x-1">
          <PrimeButton outlined severity="secondary" onClick={async () => await vm.export('csv')}
            className="!border-transparent !bg-transparent">
            <FileInput className="w-4 h-4 mr-1.5" />
            Export
          </PrimeButton>
        </div>
        <DataGrid
          pageSize={vm.pageSize}
          dataSource={vm.items}
          totalRowsCount={vm.totalRowsCount}
          searchRequest={vm.searchRequest}
          onRefresh={() => vm.getPagedAsync()}
          selectionMode='single'
          onRowSelect={onRowSelect}
        >
          <Column field="submissionId" header="ID" sortable={true} hidden={false} />
          <Column field="received" header="DATE RECEIVED" sortable={true} body={receivedDateTemplate} />
          <Column field="retirementEffDate" header="RETIREMENT DATE" sortable={true} body={retirementEffectiveDateTemplate} />
          <Column field="firstName" header="FIRST NAME" sortable={true} />
          <Column field="lastName" header="LAST NAME" sortable={true} />
          <Column field="phone" header="PHONE" sortable={true} body={(row: SubmissionListModel) => Helper.formatPhone(row.phone!)} />
          <Column field="email" header="EMAIL" sortable={true} />
          {/* {Appl.Setting.EnvironmentName !== "prod" && <Column field="memberId" header="PIN" sortable={true} />}
          {Appl.Setting.EnvironmentName === "prod" && <Column field="pin" header="PIN" sortable={true} />} */}
          <Column field="submissionStatus" header="APPLICATION STATUS" sortable={true} />
        </DataGrid>
      </View>
    </>
  );
});
